import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { opacityInOut } from '../../animations/opacity-in-out.animation';
import { scaleInOut } from '../../animations/scale-in-out.animation';
import { ApiService } from 'src/app/core/services/api.service';
import { FormControl, Validators } from '@angular/forms';
import { BehaviorSubject, Subscription, debounceTime, firstValueFrom } from 'rxjs';
import { HelperService } from 'src/app/core/services/helper.service';

const CHUNCK_SIZE = 20;

@Component({
  selector: 'confirm-hire-list',
  templateUrl: 'confirm-hire-list.component.html',
  styleUrls: ['confirm-hire-list.component.scss'],
  animations: [ opacityInOut, scaleInOut ]
})
export class ConfirmHireListComponent implements OnInit, OnDestroy {
  @Input() title: string = '';
  @Input() vacancyId = null;
  @Output() onClose = new EventEmitter();
  searchControl = new FormControl('');
  listControl = new FormControl(null, [ Validators.required ]);
  loading = true;
  talentsList = [];
  filterParams$ = new BehaviorSubject<any>({ 
    toPush: false,
    filters: { query: null, take: CHUNCK_SIZE, skip: 0 }
  });
  private currentPage = 0;
  private searchControlSub: Subscription;

  constructor(private apiService: ApiService, private helperService: HelperService) {
  }

  ngOnInit(): void {
    if (this.vacancyId) {
      this.helperService.blockScroll$.next(true);
      this.getTalents();
      this.searchControlSub = this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe(query => {
        const current = this.filterParams$.value.filters;
        const applyQuery = query && query.length > 1;
        const filterParams = { toPush: false, filters: { ...current, query: applyQuery ? query : null, skip: 0 }};
        if (!applyQuery) { this.currentPage = 0 };
        this.filterParams$.next(filterParams);
        this.getTalents();
      });
    }
  }

  private getTalents() {
    const filterParams = this.filterParams$.value.filters;
    const toPush = this.filterParams$.value.toPush;
    this.apiService.getInterestedTalents(this.vacancyId, filterParams).subscribe( {
      next: async (results) => {
        try {
          const likedCandidates = await this.getLikedMatchedTalents();

          if (results && results.length < CHUNCK_SIZE) { this.currentPage = null;}

          const baseList = toPush ? [...this.talentsList, ...(results || [])] : (results || []);

          this.talentsList = [
            ...baseList,
            ...likedCandidates.filter(liked =>
              !baseList.some(existing => existing.talentId === liked.talentId)
            )
          ];

          this.loading = false;
        } catch (err) {
          console.error('Failed to load liked candidates for confirm hire', err);

          if (results && results.length < CHUNCK_SIZE) { this.currentPage = null; }

          this.talentsList = toPush ? [...this.talentsList, ...( results || [])] : ( results || []);
          this.loading = false;
        }
      },
    });
  }

  async getLikedMatchedTalents(){
    const response = await firstValueFrom(this.apiService.getMatchedTalents(this.vacancyId, { take: 500, skip: 0, candidateFilter: 'liked' }));
    const likedCandidates = (response?.smartMatchResults || []).filter(candidate => 
      Array.isArray(candidate?.ranking?.users) && candidate.ranking.users.length > 0).map(item => ({
        talentId: item.talent?.id,
        name: item.talent?.name || `${item.talent?.firstName || ''} ${item.talent?.lastName || ''}`.trim(),
        salary: item.talent?.salary ?? null,
        matchScore: item.totalMatch || 0,
        isLiked: true,
        ranking: item.ranking
      }));
    return (likedCandidates || []);
  }

  onScrollDown(e) {
    if (this.currentPage !== null) {
      this.currentPage = this.currentPage + 1;
      const currentParams = this.filterParams$.value.filters;
      this.filterParams$.next({ toPush: true, filters: { ...currentParams, skip: this.currentPage * CHUNCK_SIZE }});
    }
  }

  onChange(e: any) {
    const value = e.target.value;
    this.listControl.setValue(value);
  }

  close() {
    this.onClose.emit();
    this.helperService.blockScroll$.next(false);
  }

  confirm() {
    if (this.listControl.valid && this.listControl.value) {
      this.onClose.emit(this.listControl.value);
      this.helperService.blockScroll$.next(false);
    }
  }

  ngOnDestroy(): void {
    if (this.searchControlSub) { this.searchControlSub.unsubscribe(); }
  }

}
